import React, { useState } from 'react';
import TopBar from '../components/TopBar';
import VideoUpload from '../components/VideoUpload';
import VideoList from '../components/VideoList';
import VideoModal from '../components/VideoModal';
import type { Video } from '../services/videoService';

const VideosPage: React.FC = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleUploadComplete = (video: Video) => {
    setShowUploadModal(false);
    // Trigger refresh of video list
    setRefreshTrigger((prev) => prev + 1);
    // Show success notification
    showNotification(`Video "${video.name}" uploaded successfully!`, 'success');
  };

  const handleUploadError = (error: string) => {
    console.error('Upload error:', error);
    showNotification(`Upload failed: ${error}`, 'error');
  };

  const handleVideoSelect = (video: Video) => {
    setSelectedVideo(video);
  };

  const handleVideoDelete = () => {
    setSelectedVideo(null);
    // Trigger refresh of video list
    setRefreshTrigger((prev) => prev + 1);
    showNotification('Video deleted successfully', 'success');
  };

  const showNotification = (message: string, type: 'success' | 'error') => {
    // TODO: Implement a proper toast notification system
    // For now, using alert
    if (type === 'success') {
      alert(message);
    } else {
      alert(`Error: ${message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <TopBar />

      {/* Main Content */}
      <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Video Library</h1>
              <p className="text-gray-600 mt-2">
                Manage your video content library
              </p>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Upload Video
            </button>
          </div>
        </div>

        {/* Video List */}
        <VideoList
          onVideoSelect={handleVideoSelect}
          refreshTrigger={refreshTrigger}
        />

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowUploadModal(false)}
            />

            {/* Modal */}
            <div className="relative min-h-screen flex items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">Upload Video</h2>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Upload Component */}
                <div className="flex-1 overflow-y-auto p-6">
                  <VideoUpload
                    onUploadComplete={handleUploadComplete}
                    onUploadError={handleUploadError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Details Modal */}
        <VideoModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
          onDelete={handleVideoDelete}
        />
      </div>
      </div>
    </div>
  );
};

export default VideosPage;
